import { createAction } from '@reduxjs/toolkit'
import { impersonationSelector } from 'admin/services/selectors'
import { firstValueFrom } from 'rxjs'
import { defaultPostToDestinationsSelector, getDestinations } from 'services/destinations'
import { updateAllPlansTimezones } from 'services/plan'
import { currentUserSelector } from 'services/users/selectors'
import { destinationsAdapter, listsAdapter, PostDestination } from 'shared'
import StoreState, { createAppAsyncThunk } from 'store/state'
import { mapAccountResponse, teamMemberAdapter } from './adapters'
import { V1 } from './net'
import { defaultTimezoneSelector } from './selectors'

export const getAccountSuccess = createAction(
  'ACTION_GET_ACCOUNT_SUCCESS',
  (payload: any) => ({ payload })
)

export const getAccountInfo = createAppAsyncThunk(
  'settings/getAccount',
  async (_, { dispatch, getState }) => {
    const isImpersonating = impersonationSelector(getState() as StoreState)
    const response = await firstValueFrom(dispatch(V1.getAccountInfo()))
    const data = dispatch(mapAccountResponse(response))
    const payload = { ...data, isImpersonating: Boolean(isImpersonating), raw: response }
    dispatch(getAccountSuccess(payload))
    return payload
  }
)

export const downgradeFree = createAppAsyncThunk(
  'settings/downgradeFree',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const appId = currentUserSelector(getState() as StoreState)?.account.id + ''
      return await firstValueFrom(dispatch(V1.downgradeFree(appId)))
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const getDefaults = createAppAsyncThunk(
  'settings/defaults/get',
  async (_, { dispatch }) => {
    const response = await firstValueFrom(dispatch(V1.getDefaults()))
    const pages = destinationsAdapter(response.pages || [])
    const connectedPageIds = pages.filter(p => p.connected).map(p => p.id)

    return {
      pages,
      lists: listsAdapter(response.lists || [], connectedPageIds),
      timezone: response.userTimezone || '',
      timezones: response.timezones.reduce((zonesObject: { [key: string]: string }, current) => {
        zonesObject[current.zone] = current.label
        return zonesObject
      }, {})
    }
  }
)

export const ACTION_GET_SETTINGS_DEFAULTS_SUCCESS = 'ACTION_GET_SETTINGS_DEFAULTS_SUCCESS'

export const updateDefaults = createAppAsyncThunk(
  'settings/defaults/update',
  async (args: { profileIds?: string[], timezone?: string, setPlansTimezones?: boolean }, { dispatch, getState }) => {
    const { profileIds, timezone, setPlansTimezones } = args
    const state = getState() as StoreState
    const ids = profileIds || Object.keys(defaultPostToDestinationsSelector(state))
    const zone = timezone || defaultTimezoneSelector(state)

    await firstValueFrom(dispatch(V1.updateDefaults(ids, zone, setPlansTimezones)))
    if (timezone && setPlansTimezones) {
      dispatch(updateAllPlansTimezones(timezone))
    }
    dispatch(getDestinations(true))
    const payload = { profileIds: ids, timezone: zone }
    return payload
  }
)

export const updateGroupPublisher = createAppAsyncThunk(
  'settings/networks/updateGroupPublisher',
  async (args: { ppid: string, publishBy: string }, { dispatch }) => {
    await firstValueFrom(dispatch(V1.updateGroupPublisher(args.ppid, args.publishBy)))
  }
)

export const getTeamSettings = createAppAsyncThunk(
  'settings/getTeam',
  async (_, { dispatch }) => {
    const response = await firstValueFrom(dispatch(V1.getTeam()))
    return {
      friends: response.friends,
      isInvited: response.isInvited,
      masterName: response.masterName,
      adminsLimit: parseInt(response.adminsLimit, 10),
      members: Object.keys(response.permissions).reduce((members: any, id) => {
        const current = teamMemberAdapter(response.permissions[id])
        members[id] = { ...current, userId: id }
        return members
      }, {})
    }
  }
)

export const addTeamMember = createAppAsyncThunk(
  'settings/addTeamMember',
  async (args: { email: string, profiles: PostDestination[] }, { dispatch, rejectWithValue }) => {
    const ppIds = args.profiles.map(p => p.ppid)
    try {
      const data = await firstValueFrom(dispatch(V1.addTeamMember(args.email, ppIds)))
      const accounts = Object.keys(data.permissions).reduce((formatted: any, id: string) => {
        const key = id.indexOf('_') === -1 ? id : id.split('_')[1]
        formatted[key] = data.permissions[id]
        return formatted
      }, {})

      return teamMemberAdapter({ ...data, accounts })
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const removeTeamMember = createAppAsyncThunk(
  'settings/removeTeamMember',
  async (id: string, { dispatch, rejectWithValue }) => {
    const response = await firstValueFrom(dispatch(V1.removeTeamMember(id)))
    if (response.error) {
      return rejectWithValue(response.error)
    }
    return id
  }
)

export const addMemberPermission = createAppAsyncThunk(
  'settings/addMemberPermission',
  async (args: { memberId: string, memberName: string, ppid: string, memberFbId?: string }, { dispatch }) => {
    const response = await firstValueFrom(dispatch(V1.addPermission(args.memberId, args.memberName, args.ppid, args.memberFbId)))
    return { memberId: args.memberId, ppid: args.ppid, permissionId: response.permissionId }
  }
)

export const removeMemberPermission = createAppAsyncThunk(
  'settings/removeMemberPermission',
  async (args: { memberId: string, permissionId: string, ppid: string }, { dispatch, rejectWithValue }) => {
    const { memberId, permissionId, ppid } = args
    const response = await firstValueFrom(dispatch(V1.removePermission(permissionId, ppid)))
    if (response.error) {
      return rejectWithValue(response.error)
    }
    return { memberId, ppid }
  }
)

export const updateInstagramConnectionMethod = createAppAsyncThunk(
  'settings/updateInstagramConnectionMethod',
  async (connectionMethod: 'direct' | 'facebook', { dispatch, rejectWithValue }) => {
    try {
      const response = await firstValueFrom(dispatch(V1.updateInstagramConnectionMethod(connectionMethod)))
      return response
    } catch (error) {
      return rejectWithValue(error.response.error)
    }
  }
)

export const getInstagramConnectionMethod = createAppAsyncThunk(
  'settings/getInstagramConnectionMethod',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await firstValueFrom(dispatch(V1.getInstagramConnectionMethod()))
      return response
    } catch (error) {
      return rejectWithValue(error.response.error)
    }
  }
)
